import { useState, useEffect, useRef } from 'react';
import { Analytics } from "@vercel/analytics/react";
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import Logo from './landing/components/Logo';
import { CAPTURE } from './lib/capture';

// Routes that need an account; logged-out visitors are sent to register.
const PROTECTED_ROUTES = {
  fullLogUpload: true,
  analytics: false,
  'saved-ledgers': false,
  bankroll: false,
  ledger: false
};

const NAV_LINKS = [
  { name: 'Upload log', page: 'fullLogUpload' },
  { name: 'Analytics', page: 'analytics' },
  { name: 'Ledger', page: 'ledger' },
  { name: 'Saved ledgers', page: 'saved-ledgers' },
  { name: 'Bankroll', page: 'bankroll' },
  { name: 'Pricing', page: 'payment' },
];

const MENU_LINKS = [...NAV_LINKS, { name: 'Features', page: 'features' }, { name: 'FAQ', page: 'faq' }];

const hrefFor = page => (page === 'home' ? '/' : `/${page}`);

const Navbar = ({ setCurrentPage, currentPage }) => {
  const { currentUser, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', y => setScrolled(y > 12));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Full-screen menu: lock page scroll and close on Escape.
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKey = e => e.key === 'Escape' && setIsMenuOpen(false);
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [isMenuOpen]);

  const handlePageChange = (page) => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
    setCurrentPage(PROTECTED_ROUTES[page] && !currentUser ? 'register' : page);
    window.scrollTo({ top: 0 });
  };

  const linkProps = page => ({
    href: hrefFor(page),
    onClick: e => {
      e.preventDefault();
      handlePageChange(page);
    },
    'aria-current': currentPage === page ? 'page' : undefined,
  });

  const handleLogout = async () => {
    try {
      await logout();
      handlePageChange('home');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const displayName = currentUser ? (currentUser.displayName || currentUser.email.split('@')[0]) : '';
  const solid = scrolled || isMenuOpen || currentPage !== 'home';

  return (
    <>
      <Analytics />
      <motion.header
        initial={CAPTURE ? false : { y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5 land:pt-4"
      >
        <nav
          aria-label="Main"
          className={`mx-auto flex h-14 max-w-[1480px] items-center justify-between gap-4 rounded-full pl-4 pr-2 text-pn-fg transition-[background-color,box-shadow,backdrop-filter] duration-500 ease-out ${
            solid
              ? 'bg-pn-bg/75 shadow-[inset_0_0_0_1px_rgb(var(--pn-line)/0.1),0_20px_40px_-30px_rgb(var(--pn-sunken))] backdrop-blur-xl'
              : 'bg-transparent'
          }`}
        >
          <a {...linkProps('home')} aria-current={undefined} className="pn-link -ml-1 shrink-0 px-1 py-1 text-pn-fg">
            <Logo className="h-7 w-auto sm:h-8" />
          </a>

          <ul className="hidden items-center gap-1 xl:flex">
            {NAV_LINKS.map(link => (
              <li key={link.page}>
                <a {...linkProps(link.page)} className="pn-link block px-3.5 py-2 text-[0.95rem] font-medium">
                  {link.name}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={toggleTheme}
              data-capture-hide
              aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              className="pn-link hidden h-10 w-10 items-center justify-center sm:inline-flex"
            >
              {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>

            {currentUser ? (
              <div className="relative" ref={profileMenuRef} data-capture-hide>
                <button
                  type="button"
                  onClick={() => setIsProfileOpen(open => !open)}
                  aria-expanded={isProfileOpen}
                  aria-haspopup="menu"
                  className="pn-link flex items-center gap-2 py-1 pl-1 pr-3"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-[0.7rem] bg-pn-signal font-bold text-pn-on-signal">
                    {displayName[0].toUpperCase()}
                  </span>
                  <span className="hidden max-w-[10rem] truncate text-sm font-medium text-pn-fg sm:inline">{displayName}</span>
                </button>
                <AnimatePresence>
                  {isProfileOpen && (
                    <motion.div
                      role="menu"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute right-0 mt-2 w-52 rounded-2xl bg-pn-raised p-1.5 text-sm shadow-[inset_0_0_0_1px_rgb(var(--pn-line)/0.1),0_24px_48px_-24px_rgb(var(--pn-sunken))]"
                    >
                      <a role="menuitem" {...linkProps('profile')} className="block rounded-xl px-3 py-2.5 hover:bg-pn-line/5">Profile</a>
                      <button role="menuitem" type="button" onClick={handleLogout} className="block w-full rounded-xl px-3 py-2.5 text-left text-pn-loss hover:bg-pn-line/5">
                        Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex items-center gap-1.5" data-capture-hide>
                <a {...linkProps('login')} className="pn-link hidden px-3.5 py-2 text-[0.95rem] font-medium sm:block">Sign in</a>
                <a {...linkProps('register')} className="pn-btn bg-pn-fg px-4 py-2.5 text-[0.95rem] text-pn-bg">Start free</a>
              </div>
            )}

            <button
              type="button"
              onClick={() => setIsMenuOpen(open => !open)}
              aria-expanded={isMenuOpen}
              aria-controls="site-menu"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              className="pn-link relative h-10 w-10 xl:hidden"
            >
              <span className={`absolute left-1/2 top-1/2 h-[1.5px] w-5 -translate-x-1/2 bg-current transition-transform duration-500 ease-spring ${isMenuOpen ? 'rotate-45' : '-translate-y-[4px]'}`} />
              <span className={`absolute left-1/2 top-1/2 h-[1.5px] w-5 -translate-x-1/2 bg-current transition-transform duration-500 ease-spring ${isMenuOpen ? '-rotate-45' : 'translate-y-[4px]'}`} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            id="site-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 overflow-y-auto bg-pn-bg/90 px-6 pb-10 pt-28 text-pn-fg backdrop-blur-2xl xl:hidden"
          >
            <ul className="mx-auto flex max-w-xl flex-col">
              {MENU_LINKS.map((link, i) => (
                <motion.li
                  key={link.page}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.04 * i, ease: [0.16, 1, 0.3, 1] }}
                >
                  <a {...linkProps(link.page)} className="block border-b border-pn-line/10 py-4 text-[clamp(1.6rem,6vw,2.6rem)] font-bold tracking-[-0.03em] aria-[current=page]:text-pn-signal">
                    {link.name}
                  </a>
                </motion.li>
              ))}
            </ul>
            <div className="mx-auto mt-8 flex max-w-xl items-center justify-between" data-capture-hide>
              {!currentUser && (
                <a {...linkProps('login')} className="pn-btn pn-btn-quiet">Sign in</a>
              )}
              <button type="button" onClick={toggleTheme} className="pn-btn pn-btn-quiet">
                {isDark ? 'Light theme' : 'Dark theme'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
